import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material'
import { Search, TuneOutlined } from '@mui/icons-material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import SmartMateriaUITablePagination from '../../generic/SmartMaterialUITablePagination'
import TableTune from '../../generic/TableTune'
import DateColumnFilterPopover from '../../generic/DateColumnFilterPopover'
import { usePagination } from '../../generic/hooks/usePagination'
import { useSmartInterval } from '../../generic/hooks/useSmartInterval'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { isDateColumnType } from '../../generic/utilities/tableRowSorting'
import SequencePositionCell from './SequencePositionCell'
import PlannerDetailsTableToolbar from './PlannerDetailsTableToolbar'
import { mergeColumnsAndTunedFields, tuneFieldsFromColumns } from './mergeColumnsAndTunedFields'

const PRIMARY_KEYS = ['id']
// The order a table has with no `defaultSort` from its host: the stored queue number, ascending.
// usePagination only sends a sort key that is a declared column or the default one, so the default
// is passed where it expects the queue-order column.
const DEFAULT_SORT = { field: 'order', direction: 1 }
// The virtual sort key Sun accepts for a job's place in its machine's queue (contract §1).
const POSITION_FIELD = 'position'
const NO_OVERRIDES = {}
const DEFAULT_ROWS_PER_PAGE = 10
const REFRESH_INTERVAL_MS = 1000
// Rows carry their position in the page under this key, as in SmartMateriaUITable: the row actions
// receive (and post) the row with it.
const ROW_INDEX_KEY = 'smart-table-index'
const STICKY_TOP = 'var(--smart-navigator-sticky-offset, 0px)'

// Compact rows: short values stay on one line and cells keep a narrower side padding than MUI's.
// A thin line separates the rows.
const cellStyle = {
  borderBottom: theme => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
  whiteSpace: 'nowrap',
  px: 1
}
// Story 177, PO feedback on the build: the table scrolls sideways inside its own frame. With `overflow: initial`
// (copied from SmartMateriaUITable) its seventeen columns made the whole Planner page wider than the screen.
const tableFrameStyle = {
  overflowX: 'auto',
  border: theme => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
  borderRadius: 1
}
// Row actions as small icon buttons, aligned to the right.
const actionsCellStyle = {
  position: 'sticky',
  right: 0,
  zIndex: 2,
  backgroundColor: 'inherit',
  '& .MuiIconButton-root': { p: 0.5 },
  '& .MuiSvgIcon-root': { fontSize: 20 }
}
const positionBoxStyle = {
  display: 'inline-block',
  minWidth: 36,
  px: 1,
  py: 0.25,
  border: theme => `1px solid ${alpha(theme.palette.text.primary, 0.23)}`,
  borderRadius: 1,
  textAlign: 'center',
  fontWeight: 500,
  fontVariantNumeric: 'tabular-nums'
}
const footerCellStyle = {
  borderTop: '1px solid rgba(81, 81, 81, 1)',
  backgroundColor: '#373636 !important',
  whiteSpace: 'nowrap'
}
// A finished step is dimmed across its data cells; its actions keep full strength.
const finishedCellStyle = {
  opacity: 0.6
}
const filterFieldStyle = {
  '& .MuiInput-underline:before': {
    borderBottom: '0px !important'
  },
  fontSize: '14px !important'
}
const filterIconStyle = {
  fontSize: '18px !important'
}

const HeaderCell = ({ children }) => (
  <TableCell
    sx={{
      fontSize: 14,
      fontWeight: 600,
      color: 'text.secondary',
      borderBottom: theme => `1px solid ${theme.palette.primary.main}99`,
      backgroundColor: theme => theme.palette.background.color_373636,
      verticalAlign: 'middle',
      px: 1
    }}
  >
    {children}
  </TableCell>
)

// `highlight` is the background the host asks for this row (a theme colour path such as
// 'action.selected'), drawn over the zebra one; the hover colour still wins while the pointer is on it.
const DataRow = ({ odd, highlight, onClick, children }) => (
  <TableRow
    onClick={onClick}
    sx={{
      backgroundColor: highlight || (odd
        ? theme => theme.palette.background.color_373636
        : theme => theme.palette.background.row2),
      '&:hover': {
        backgroundColor: theme => theme.palette.background.table_row_hover
      },
      transition: 'background-color 0.3s ease-out'
    }}
  >
    {children}
  </TableRow>
)

const sameSort = (first, second) => first.field === second.field && first.direction === second.direction

const getColumnType = (column, row) => (typeof column.type === 'function' ? column.type(row) : column.type)

const isDateFilterColumn = (column) => column.filterType === 'date' || isDateColumnType(getColumnType(column, null))

const isColumnSortable = (column) => column.sortable === undefined || column.sortable === true

/**
 * The columns the table draws: one per declared column, with the host's drawing overrides (its own
 * `render`, `hide` or `style`) on top. Sorting, filtering, TUNE, the query and the CSV export keep
 * working on the declared column.
 */
const buildDisplayColumns = (columns, columnOverrides) => columns.map((column) => ({
  ...column,
  ...(columnOverrides[column.field] || {}),
  sortField: column.field,
  sortable: isColumnSortable(column),
  filterColumn: column
}))

/**
 * The values a read-only multi select cell shows, from the raw row.
 */
const getMultiSelectValues = (row, column) => {
  const value = row[column.field]
  if (value === null || value === undefined) return []
  if (Array.isArray(value)) return value
  if (Object.keys(value).length === 0 && Object.getPrototypeOf(value) === Object.prototype) return []
  return [value]
}

/** A read-only list (operators, tool references) as small chips on one line when they fit. */
const ListCell = ({ values }) => (
  <Stack direction='row' flexWrap='wrap' gap={0.5}>
    {values.map((value, index) => <Chip key={index} size='small' label={value} />)}
  </Stack>
)

/**
 * The details table of the Planner, drawn with MUI's table and not with `generic/SmartMateriaUITable`.
 * It does what `generic/SmartMateriaUITable` did for `PlannerDetails.jsx` (retired), fed with the same props: server-side
 * pagination with the user's rows-per-page, header sort, general search, column filters, TUNE, CSV
 * export, import, direct row actions and a refresh about every second.
 *
 * It keeps today's colours and takes the layout of the machine queue prototype: a bar in two lines
 * (tabs on top, search and tools below), a bold header, thin lines between compact rows, lists as
 * small chips, row actions that stay on the right with the row's background when the table scrolls
 * sideways, column filters that show only the magnifier, finished steps dimmed, and optionally a
 * read-only position column and a summary next to the pager.
 *
 * Room left for the story that will connect it (built by that story, not here):
 * - `title` and `toolbar.render` take nodes, for content in the bar;
 * - `column.render(row, { showAlert })` paints a column's own cell;
 * - `rowMenu(row)` adds content after the direct row actions;
 * - `refreshPaused` stops the periodic refresh from outside. Sorting, filtering and paging still
 *   load once while it is paused.
 *
 * @param {Object} props
 * @param {String} props.tableTuneUniqueKey Key of the user's saved TUNE and rows-per-page
 * @param {Array<Object>} props.columns Column declarations, as SmartMateriaUITable takes them
 * @param {Function} props.dataFetch Called with the query builder; returns a page `{ content, totalElements, number, size, ... }`
 * @param {Object} props.toolbar `{ render: { fetchFunction }, renderSticky: { fetchFunction } }`
 * @param {Object} props.actions `{ custom: [{ name, contextMenu: false, render(row, dataFetch, queryBuilder) }] }`
 * @param {*} props.forceLoad Any value; when it changes the table loads again
 * @param {Array<Object>} props.importFile Import bean params, as today
 * @param {String|Object} props.title Shown on the left of the bar and in the TUNE dialog
 * @param {Function} props.rowMenu Optional content after the direct actions of a row
 * @param {Boolean} props.refreshPaused Stops the periodic refresh while true
 * @param {Object} props.tabs `{ items: [{ value, label, count }], value, onChange(value) }`, drawn on top of the bar
 * @param {Boolean} props.positionColumn Shows each row's position in its machine queue (Sun's `position`), first, read only
 * @param {String|Object} props.footerSummary Shown next to the pager
 * @param {*} props.pageResetKey Any value; when it changes the table goes back to the first page and loads
 * @param {Object} props.columnOverrides How to draw some columns, by field:
 *   `{ [field]: { render(row, { showAlert, formatDateTime, formatDate, formatTime }), hide, style } }`
 * @param {Object} props.defaultSort `{ field, direction }` the order the table starts in and goes back to
 *   (its "Remove sort"); when the host changes it (another tab) and the user has not chosen another
 *   order, the table follows it
 * @param {Function} props.onRowClick `(row, rowIndex)`, called when a row is clicked
 * @param {Function} props.rowBackgroundColorFunction `(row)` returns a theme colour path (or an empty value) for the row
 * @param {*} props.resetSelectedRow Any value; when it changes the clicked row is no longer marked
 * @param {*} props.resetSort Any value; when it changes (after the first render) the table drops the
 *   user's order, goes back to `defaultSort` and loads
 */
const PlannerDetailsTable = ({
  tableTuneUniqueKey = '',
  columns,
  dataFetch,
  toolbar,
  actions,
  forceLoad,
  importFile,
  title = '',
  rowMenu,
  refreshPaused = false,
  tabs,
  positionColumn = false,
  footerSummary = '',
  pageResetKey,
  columnOverrides = NO_OVERRIDES,
  defaultSort = DEFAULT_SORT,
  onRowClick,
  rowBackgroundColorFunction,
  resetSelectedRow,
  resetSort
}) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime, formatDate, formatTime } = useDateFormatter()
  const [AlertElement, showAlert] = AlertUI()

  const displayColumns = useMemo(() => buildDisplayColumns(columns, columnOverrides), [columns, columnOverrides])
  const [customColumns, setCustomColumns] = useState(displayColumns)
  const displayColumnsRef = useRef(displayColumns)
  displayColumnsRef.current = displayColumns
  const tuneLoadedRef = useRef(false)
  const [tuneOpen, setTuneOpen] = useState(false)
  const [tableTune, setTableTune] = useState([])
  const [tableTuneFields, setTableTuneFields] = useState([])

  const [data, setData] = useState([])
  const [firstLoad, setFirstLoad] = useState(false)
  const [loadTable, setLoadTable] = useState(false)
  const [pageableObject, setPageableObject] = useState({ number: 0, size: DEFAULT_ROWS_PER_PAGE })
  const [rowsPerPagePreferenceLoaded, setRowsPerPagePreferenceLoaded] = useState(tableTuneUniqueKey === '')
  const [sortColumnFieldPageable, setSortColumnFieldPageable] = useState(defaultSort)
  const [filterText, setFilterText] = useState('')
  const columnFilters = useRef({})
  const columnFilterOperators = useRef({})
  const [firstRequestPending, setFirstRequestPending] = useState(true)
  const refreshPausedRef = useRef(refreshPaused)
  refreshPausedRef.current = refreshPaused

  const requestPagination = usePagination(undefined, undefined, pageableObject, columns, PRIMARY_KEYS, sortColumnFieldPageable, undefined, filterText, columnFilters.current, defaultSort, columnFilterOperators.current)
  const requestPaginationRef = useRef(requestPagination)

  useEffect(() => {
    requestPaginationRef.current = requestPagination
  }, [requestPagination])

  useEffect(() => {
    if (tableTuneUniqueKey === '') {
      setCustomColumns(displayColumns)
    }
  }, [displayColumns, tableTuneUniqueKey])

  // The user's rows per page; 10 when there is none or it cannot be read.
  useEffect(() => {
    const applyRowsPerPage = (value) => {
      setPageableObject(previous => ({ ...previous, size: value, page: 0, number: 0 }))
    }
    if (tableTuneUniqueKey === '') {
      setRowsPerPagePreferenceLoaded(true)
      return
    }
    const getRowsPerPage = async () => {
      setRowsPerPagePreferenceLoaded(false)
      try {
        const response = await apiGet('userTableRowsPerPage/' + tableTuneUniqueKey)
        applyRowsPerPage(Number.isInteger(response) && response > 0 ? response : DEFAULT_ROWS_PER_PAGE)
      } catch (exception) {
        applyRowsPerPage(DEFAULT_ROWS_PER_PAGE)
      } finally {
        setRowsPerPagePreferenceLoaded(true)
      }
    }
    getRowsPerPage()
  }, [tableTuneUniqueKey])

  const handleRowsPerPageChanged = useCallback((value) => {
    if (tableTuneUniqueKey === '') {
      return
    }
    apiPut('userTableRowsPerPage/' + tableTuneUniqueKey, value)
  }, [tableTuneUniqueKey])

  // The saved TUNE, once per key: reading it again on every new columns array would throw away
  // changes the user is making in the dialog.
  useEffect(() => {
    tuneLoadedRef.current = false
    const applyTune = (response) => {
      const merged = mergeColumnsAndTunedFields(displayColumnsRef.current, response.fields ? response.fields : [])
      const fields = tuneFieldsFromColumns(merged)
      setTableTuneFields(fields)
      setTableTune(response.fields ? response : { id: null, tableKey: tableTuneUniqueKey, fields })
      setCustomColumns(merged)
    }
    const getTune = async () => {
      try {
        applyTune(await apiGet('user/tabletune/' + tableTuneUniqueKey))
      } catch (error) {
        applyTune({})
      }
      tuneLoadedRef.current = true
    }
    if (tableTuneUniqueKey !== '') {
      getTune()
    }
  }, [tableTuneUniqueKey])

  // New column declarations after the TUNE is loaded: keep the applied show/hide/order.
  useEffect(() => {
    if (tableTuneUniqueKey === '' || !tuneLoadedRef.current) {
      return
    }
    setCustomColumns((previous) => {
      const currentFields = previous.map((column) => ({ name: column.field, show: !column.hide, order: column.order }))
      return mergeColumnsAndTunedFields(displayColumns, currentFields)
    })
  }, [displayColumns, tableTuneUniqueKey])

  const saveTune = async () => {
    const response = await apiPost('user/tabletune', { ...tableTune, fields: tableTuneFields })
    if (response.fields) {
      setTableTune(response)
      setCustomColumns(mergeColumnsAndTunedFields(displayColumns, tableTuneFields))
    }
  }

  const closeTune = () => {
    setTableTuneFields(tuneFieldsFromColumns(customColumns))
    setTuneOpen(false)
  }

  const visibleColumns = useMemo(() => customColumns.filter((customColumn) => {
    const column = displayColumns.find((item) => item.field === customColumn.field)
    if (column === undefined || column.hide) return false
    return !customColumn.hide
  }), [customColumns, displayColumns])

  const getIntervalTableData = async () => {
    if (tableTuneUniqueKey !== '' && !rowsPerPagePreferenceLoaded) return []
    if (requestPagination.size === undefined || requestPagination.size === null) return []
    if (Object.keys(requestPagination).length === 0) return []
    let rows
    try {
      rows = await dataFetch(requestPagination)
    } catch (error) {
      // As in today's table, a failed load leaves the table as it was and ends the periodic refresh:
      // useSmartInterval only schedules the next tick after a load that finishes, so this one never
      // does. Today the failure also escapes as an unhandled promise; here it is logged instead.
      console.error('Error loading the planner details:', error)
      return new Promise(() => {})
    }
    if (rows === undefined) {
      setFirstLoad(true)
      return []
    }
    const pageMetadata = { ...rows }
    if (pageMetadata.content !== undefined) {
      rows = pageMetadata.content
    }
    delete pageMetadata.content
    // A page or size changed while the request was in flight: this answer is stale.
    if (requestPagination.page === requestPaginationRef.current.page &&
        requestPagination.size === requestPaginationRef.current.size) {
      setPageableObject(pageMetadata)
    }
    setFirstLoad(true)
    return rows.map((row, index) => ({ ...row, [ROW_INDEX_KEY]: index }))
  }

  const updateTableData = (rows) => {
    if (requestPagination.page !== requestPaginationRef.current.page ||
        requestPagination.size !== requestPaginationRef.current.size) {
      return
    }
    setData(rows)
  }

  const startTableInterval = useSmartInterval(getIntervalTableData, REFRESH_INTERVAL_MS, updateTableData)
  const startTableIntervalRef = useRef()

  useEffect(() => {
    startTableIntervalRef.current = startTableInterval
  }, [startTableInterval])

  // Load now when the host asks for it (the Timeline filters, a plan write) or the query changes (sort, search,
  // page, filters -- the user's own actions, which load once even while paused). What the host asks while the
  // refresh is paused waits: the pause ending is what loads, once, with the latest state (D-X, RN35).
  const lastLoadSignalsRef = useRef({ forceLoad, loadTable, refreshPaused })
  const loadedFirstTimeRef = useRef(false)
  useEffect(() => {
    const last = lastLoadSignalsRef.current
    lastLoadSignalsRef.current = { forceLoad, loadTable, refreshPaused }
    const resumed = last.refreshPaused && !refreshPaused
    const queryChanged = last.loadTable !== loadTable
    const hostAsked = last.forceLoad !== forceLoad
    const firstTime = !loadedFirstTimeRef.current
    loadedFirstTimeRef.current = true
    const loadNow = firstTime || queryChanged || resumed || (hostAsked && !refreshPaused)
    startTableIntervalRef.current(!refreshPaused, loadNow)
  }, [forceLoad, loadTable, refreshPaused])

  useEffect(() => {
    if (Object.keys(requestPagination).length !== 0 && firstRequestPending) {
      startTableIntervalRef.current(!refreshPausedRef.current, true)
      setFirstRequestPending(false)
    }
  }, [requestPagination, firstRequestPending])

  // A new tab (or whatever the host keys it on) starts again from the first page.
  const pageResetKeyRef = useRef(pageResetKey)
  useEffect(() => {
    if (pageResetKeyRef.current === pageResetKey) return
    pageResetKeyRef.current = pageResetKey
    setPageableObject(previous => (previous.number === 0 ? previous : { ...previous, number: 0 }))
    setLoadTable(previous => !previous)
  }, [pageResetKey])

  // The host's default order changed (another tab): follow it, unless the user chose another order.
  // A sort by position only makes sense where the position is, so it never outlives its tab.
  const defaultSortRef = useRef(defaultSort)
  defaultSortRef.current = defaultSort
  const [followedDefaultSort, setFollowedDefaultSort] = useState(defaultSort)
  if (!sameSort(followedDefaultSort, defaultSort)) {
    setFollowedDefaultSort(defaultSort)
    setSortColumnFieldPageable(current => (
      sameSort(current, followedDefaultSort) || (current.field === POSITION_FIELD && defaultSort.field !== POSITION_FIELD) ? defaultSort : current
    ))
  }

  // "Remove sort": back to the current default order, loading at once.
  const resetSortInitializedRef = useRef(false)
  useEffect(() => {
    if (!resetSortInitializedRef.current) {
      resetSortInitializedRef.current = true
      return
    }
    setSortColumnFieldPageable(defaultSortRef.current)
    setLoadTable(previous => !previous)
  }, [resetSort])

  const [selectedRowIndex, setSelectedRowIndex] = useState(-1)
  useEffect(() => {
    setSelectedRowIndex(-1)
  }, [resetSelectedRow])

  const handleRowClick = (row, rowIndex) => {
    setSelectedRowIndex(rowIndex)
    onRowClick(row, rowIndex)
  }

  const handleColumnHeaderClick = (column) => {
    setSortColumnFieldPageable(previous => column.field === previous.field
      ? { field: previous.field, direction: previous.direction * -1 }
      : { field: column.field, direction: -1 })
    setLoadTable(previous => !previous)
  }

  const changeSearchText = (text) => {
    setLoadTable(previous => !previous)
    setFilterText(text)
  }

  // A new filter value goes back to the first page; clearing one keeps the page, as today.
  const changeColumnFilter = (column, value) => {
    if (value !== '' && pageableObject.number !== 0) {
      setPageableObject(previous => ({ ...previous, number: 0 }))
    }
    columnFilters.current = { ...columnFilters.current, [column.field]: value }
    setLoadTable(previous => !previous)
  }

  const applyColumnFilter = useCallback((field, operator, value) => {
    columnFilters.current = { ...columnFilters.current, [field]: value }
    columnFilterOperators.current = { ...columnFilterOperators.current, [field]: operator }
    setPageableObject((previous) => (previous.number === 0 ? previous : { ...previous, number: 0 }))
    setLoadTable((previous) => !previous)
  }, [])

  const removeColumnFilter = useCallback((field) => {
    const nextFilters = { ...columnFilters.current }
    const nextOperators = { ...columnFilterOperators.current }
    delete nextFilters[field]
    delete nextOperators[field]
    columnFilters.current = nextFilters
    columnFilterOperators.current = nextOperators
    setPageableObject((previous) => (previous.number === 0 ? previous : { ...previous, number: 0 }))
    setLoadTable((previous) => !previous)
  }, [])

  const tableRef = useRef()
  const tableContainerRef = useRef()
  // The table scrolls sideways inside its own frame, so the sticky bar above it stays as wide as the block
  // (story 177: following a wider table made the whole Planner page wider than the screen).

  const readCellValue = (row, column) => {
    const path = column.field.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '').split('.')
    let value = row
    for (const key of path) {
      if (value === null || value === undefined || value === 'undefined' || !(key in value) || value[key] === null) {
        return undefined
      }
      value = value[key]
    }
    if (value === '') return ''
    const type = getColumnType(column, null)
    if (type === 'datetime' || type === 'dateTimePicker') return formatDateTime(value)
    if (type === 'date' || type === 'datePicker') return formatDate(value)
    if (type === 'time') return formatTime(value)
    return value
  }

  // Boolean columns are not drawn: the details table has none. Lists are lists of texts.
  const renderCell = (row, column) => {
    if (column.render) {
      return column.render(row, { showAlert, formatDateTime, formatDate, formatTime })
    }
    const value = readCellValue(row, column)
    const type = getColumnType(column, row)
    if ((value || value === 0) && type !== 'smartMultiSelect' && type !== 'boolean') {
      return <Typography variant='body2' color={typeof column.color === 'function' ? column.color(row) : column.color ? column.color : undefined}>{value}</Typography>
    }
    if (type === 'smartMultiSelect') {
      return <ListCell values={getMultiSelectValues(row, column)} />
    }
    return ''
  }

  const directActions = (actions?.custom ?? []).filter(action => action.visible !== false && action.contextMenu === false && action.render)
  const hasActionsColumn = (actions?.custom?.length ?? 0) > 0 || typeof rowMenu === 'function'
  const hasRows = data.length > 0
  const leadingColumns = positionColumn ? 1 : 0
  const allColumns = leadingColumns + visibleColumns.length + (hasActionsColumn ? 1 : 0)
  const dimmedWhenFinished = (row) => (row.finished === true ? finishedCellStyle : {})

  return (
    <Paper sx={{ background: theme => theme.palette.background.color_373636 }}>
      {AlertElement}
      <SmartDialog
        setOpen={tuneOpen}
        close={false}
        accept
        cancelCallback={closeTune}
        acceptCallback={() => { saveTune(); setTuneOpen(false) }}
        cancel
        closeCallback={closeTune}
        title={createDialogTitle({ color: 'primary', title, icon: TuneOutlined })}
        renderComponent={<TableTune fields={tableTuneFields} onChange={setTableTuneFields} />}
      />
      <div style={{ position: 'relative' }}>
        <Box sx={{
          position: 'sticky',
          top: STICKY_TOP,
          zIndex: 10,
          backgroundColor: theme => theme.palette.background.color_373636
        }}
        >
          <PlannerDetailsTableToolbar
            title={title}
            tabs={tabs}
            toolbar={toolbar}
            columns={columns}
            importFile={importFile}
            tuneEnabled={tableTuneUniqueKey !== ''}
            onTuneClick={() => setTuneOpen(true)}
            onSearchChange={changeSearchText}
            dataFetch={dataFetch}
            requestPagination={requestPagination}
          />
        </Box>
        <TableContainer ref={tableContainerRef} sx={tableFrameStyle}>
          <Table stickyHeader size='small' ref={tableRef} sx={{ width: '100%' }}>
            {/* The table scrolls inside its own frame, so the header sticks to the top of that frame (0), not below the
                page's sticky bar: with the page offset it slid down over the rows (PO, story 177). */}
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: theme => theme.palette.background.color_373636 }}>
              <TableRow>
                {positionColumn ? <TableCell sx={{ px: 1 }} /> : null}
                {visibleColumns.map((column, index) => {
                  const filterColumn = column.filterColumn
                  if (filterColumn === undefined) return <TableCell key={'filterRow_' + index} sx={{ px: 1 }} />
                  if (filterColumn.search === null) return null
                  if (filterColumn.search === false || filterColumn.disableFilter) return <TableCell key={'filterRow_' + index} sx={{ px: 1 }} />
                  return (
                    <TableCell key={'filterRow_' + index} sx={{ px: 1 }}>
                      {isDateFilterColumn(filterColumn)
                        ? (
                          <DateColumnFilterPopover
                            column={filterColumn}
                            storedValue={columnFilters.current[filterColumn.field]}
                            storedOperator={columnFilterOperators.current[filterColumn.field]}
                            onApply={applyColumnFilter}
                            onClear={removeColumnFilter}
                          />
                          )
                        : (
                          <TextField
                            variant='standard'
                            sx={{ ...filterFieldStyle, width: filterColumn.width }}
                            inputProps={{ 'aria-label': t_('Search') + ' ' + column.name }}
                            disabled={filterColumn.searchable ? false : !isColumnSortable(filterColumn)}
                            onChange={(event) => changeColumnFilter(filterColumn, event.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position='start'>
                                  <Search sx={filterIconStyle} />
                                </InputAdornment>
                              )
                            }}
                          />
                          )}
                    </TableCell>
                  )
                })}
                {hasActionsColumn ? <TableCell /> : null}
              </TableRow>
              <TableRow>
                {positionColumn
                  ? (
                    <HeaderCell>
                      <TableSortLabel
                        active={sortColumnFieldPageable.field === POSITION_FIELD}
                        direction={sortColumnFieldPageable.direction === -1 ? 'desc' : 'asc'}
                        onClick={() => handleColumnHeaderClick({ field: POSITION_FIELD })}
                      >
                        {t_('Position')}
                      </TableSortLabel>
                    </HeaderCell>
                    )
                  : null}
                {visibleColumns.map((column, index) => (
                  <HeaderCell key={'header_' + index}>
                    {column.sortable
                      ? (
                        <TableSortLabel
                          active={column.sortField === sortColumnFieldPageable.field}
                          direction={sortColumnFieldPageable.direction === -1 ? 'desc' : 'asc'}
                          onClick={() => handleColumnHeaderClick({ field: column.sortField })}
                          disabled={column.filterColumn?.disableFilter}
                        >
                          {column.name}
                        </TableSortLabel>
                        )
                      : column.name}
                  </HeaderCell>
                ))}
                {hasActionsColumn ? <HeaderCell /> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {!firstLoad
                ? <TableRow><TableCell align='center' sx={{ paddingTop: '2rem', paddingBottom: '2rem' }} colSpan={leadingColumns + visibleColumns.length}><CircularProgress /></TableCell></TableRow>
                : null}
              {data.map((row, rowIndex) => (
                <DataRow
                  key={rowIndex}
                  odd={rowIndex % 2 !== 0}
                  highlight={(typeof rowBackgroundColorFunction === 'function' ? rowBackgroundColorFunction(row) : '') || (selectedRowIndex === rowIndex ? 'action.selected' : '')}
                  onClick={typeof onRowClick === 'function' ? () => handleRowClick(row, rowIndex) : undefined}
                >
                  {positionColumn
                    ? (
                      <TableCell sx={{ ...cellStyle, ...dimmedWhenFinished(row) }}>
                        <SequencePositionCell row={row} sx={positionBoxStyle} />
                      </TableCell>
                      )
                    : null}
                  {visibleColumns.map((column, columnIndex) => (
                    <TableCell key={'data' + rowIndex + '_column' + columnIndex} sx={{ ...cellStyle, ...(column.style || {}), ...dimmedWhenFinished(row) }}>
                      <Box>{renderCell(row, column)}</Box>
                    </TableCell>
                  ))}
                  {hasActionsColumn
                    ? (
                      <TableCell sx={{ ...cellStyle, ...actionsCellStyle }}>
                        <Stack direction='row' justifyContent='flex-end' alignItems='center'>
                          <Stack direction='row' justifyContent='flex-end' alignItems='center'>
                            {directActions.map((action, index) => {
                              const content = action.render(row, dataFetch, requestPagination)
                              return content ? <div key={'direct-icon-' + index}>{content}</div> : null
                            })}
                            {typeof rowMenu === 'function' ? rowMenu(row) : null}
                          </Stack>
                        </Stack>
                      </TableCell>
                      )
                    : null}
                </DataRow>
              ))}
              {!hasRows && firstLoad
                ? <TableRow><TableCell align='center' sx={{ paddingTop: '2rem', paddingBottom: '2rem' }} colSpan={leadingColumns + visibleColumns.length}><Typography color='primary' fontSize='1.5rem'>{t_('No data to display')}</Typography></TableCell></TableRow>
                : null}
            </TableBody>
            {rowsPerPagePreferenceLoaded
              ? (
                <TableFooter>
                  <TableRow>
                    {footerSummary
                      ? (
                        <TableCell colSpan={Math.max(1, Math.floor(allColumns / 2))} sx={footerCellStyle}>
                          <Typography variant='caption' color='text.secondary'>{footerSummary}</Typography>
                        </TableCell>
                        )
                      : null}
                    {/* page, rowsPerPage and the setters for them are only used by client-side tables */}
                    <SmartMateriaUITablePagination
                      pageable
                      pageableObject={pageableObject}
                      setPageableObject={setPageableObject}
                      onRowsPerPageChange={handleRowsPerPageChanged}
                      editIndex={-2}
                      loadTable={loadTable}
                      setLoadTable={setLoadTable}
                      page={0}
                      rowsPerPage={pageableObject.size}
                      setPage={() => {}}
                      setRowsPerPage={() => {}}
                      getTableDataFiltered={() => data}
                    />
                  </TableRow>
                </TableFooter>
                )
              : null}
          </Table>
        </TableContainer>
      </div>
    </Paper>
  )
}

export default PlannerDetailsTable
